import type { FlowRoute, StepViewProps } from './flow-route-types.js';
import {
  dayLabels,
  fulfillmentMethodLabels,
  labelOrDash,
  paymentMethodLabels,
  textOrDash,
} from './format.js';
import { Notice } from './notice.jsx';
import { paths } from './paths.js';
import type { OnboardingDraft } from './onboarding-state.js';
import { useOnboarding } from './use-onboarding.jsx';
import type { ConsoleConnection } from './console-api.js';

export function StepReview({ route, steps, onNavigate }: StepViewProps) {
  const { draft, connection } = useOnboarding();
  // Only the steps *before* this one. Reviewing happens before activating, so listing "Ativar"
  // as a pendency on the review screen would be a permanent, unresolvable warning.
  const reviewIndex = steps.findIndex((step) => step.path === route.path);
  const pendingSteps = steps
    .slice(0, reviewIndex)
    .filter((step) => step.kind === 'step' && !step.isComplete({ draft, connection }));

  return (
    <div className="flex flex-col gap-8">
      <Pendencias pendingSteps={pendingSteps} onNavigate={onNavigate} />

      <SummaryBlock title="Seu mercado" onEdit={() => onNavigate(paths.store)}>
        <Row label="Nome" value={textOrDash(draft.store.name)} />
        <Row label="Endereço" value={textOrDash(draft.store.formattedAddress)} />
        <Row label="Ponto no mapa" value={locationOf(draft)} />
      </SummaryBlock>

      <SummaryBlock title="WhatsApp" onEdit={() => onNavigate(paths.whatsappConnect)}>
        <Row label="Conta Meta Business" value={contaOf(connection)} />
        <Row label="Número" value={numeroOf(connection)} />
      </SummaryBlock>

      <SummaryBlock title="Pagamento e entrega" onEdit={() => onNavigate(paths.service)}>
        <Row label="Pagamentos" value={paymentMethodsOf(draft)} />
        <Row label="Entrega/retirada" value={fulfillmentMethodsOf(draft)} />
        <Row label="Área de entrega" value={deliveryAreaOf(draft)} />
        <Row label="Observações" value={textOrDash(draft.service.deliveryNotes)} />
        <HorariosRow draft={draft} />
      </SummaryBlock>
    </div>
  );
}

function Pendencias({
  pendingSteps,
  onNavigate,
}: {
  pendingSteps: FlowRoute[];
  onNavigate: (path: string) => void;
}) {
  if (pendingSteps.length === 0) {
    return <Notice tone="success" title="Tudo pronto para iniciar o teste grátis">
      Revise os dados abaixo e siga para activate seus 7 dias de teste.
    </Notice>;
  }

  return (
    <Notice tone="warning" title="Faltam informações antes de iniciar o teste">
      <ul className="mt-2 flex flex-col gap-2">
        {pendingSteps.map((step) => (
          <li key={step.path} className="flex flex-wrap items-center gap-2">
            <span>{step.title}</span>
            <button
              className="btn btn-ghost btn-xs"
              type="button"
              onClick={() => onNavigate(step.path)}
            >
              Ir para
            </button>
          </li>
        ))}
      </ul>
    </Notice>
  );
}

function SummaryBlock({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-box border border-base-300 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="font-semibold">{title}</h2>
        <button className="btn btn-ghost btn-xs" type="button" onClick={onEdit}>
          Editar
        </button>
      </div>
      <dl className="grid gap-2 sm:grid-cols-[10rem_1fr]">{children}</dl>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-sm opacity-60">{label}</dt>
      <dd className="text-sm break-words">{value}</dd>
    </>
  );
}

function HorariosRow({ draft }: { draft: OnboardingDraft }) {
  const schedules = schedulesOf(draft);

  if (schedules.length === 0) {
    return <Row label="Horários" value="—" />;
  }

  return (
    <>
      <dt className="text-sm opacity-60">Horários</dt>
      <dd className="text-sm break-words">
        <ul className="flex flex-col gap-1">
          {schedules.map((schedule) => (
            <li key={schedule}>{schedule}</li>
          ))}
        </ul>
      </dd>
    </>
  );
}

function locationOf(draft: OnboardingDraft): string {
  if (!draft.store.latitude || !draft.store.longitude) {
    return '—';
  }

  return 'Confirmado no mapa';
}

function paymentMethodsOf(draft: OnboardingDraft): string {
  if (draft.service.paymentMethods.length === 0) {
    return '—';
  }

  return draft.service.paymentMethods.map((method) => labelOrDash(paymentMethodLabels, method)).join(', ');
}

function fulfillmentMethodsOf(draft: OnboardingDraft): string {
  if (draft.service.fulfillmentMethods.length === 0) {
    return '—';
  }

  return draft.service.fulfillmentMethods.map((method) => labelOrDash(fulfillmentMethodLabels, method)).join(', ');
}

function deliveryAreaOf(draft: OnboardingDraft): string {
  if (!draft.service.deliveryArea.enabled) {
    return 'desativada';
  }

  const parts: string[] = [];

  if (draft.service.deliveryArea.radiusKm !== undefined) {
    parts.push(`até ${draft.service.deliveryArea.radiusKm} km`);
  }

  if (draft.service.deliveryArea.neighborhoods.length > 0) {
    parts.push(`bairros: ${draft.service.deliveryArea.neighborhoods.join(', ')}`);
  }

  if (parts.length === 0) {
    return '—';
  }

  return parts.join('; ');
}

function schedulesOf(draft: OnboardingDraft): string[] {
  return draft.service.schedules
    .filter((schedule) => schedule.isOpen)
    .map((schedule) => `${dayLabels[schedule.day]} ${schedule.opensAt}–${schedule.closesAt}`);
}

function contaOf(connection: ConsoleConnection | null): string {
  if (!connection) {
    return '—';
  }

  return connection.verifiedName ?? connection.wabaId;
}

function numeroOf(connection: ConsoleConnection | null): string {
  if (!connection?.displayPhoneNumber) {
    return '—';
  }

  return connection.displayPhoneNumber;
}
